import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button, HStack, Input, Spinner, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { deleteCloudSpace, makeLinkCode, readServerInfo, ServerError, type ServerLink } from '../data/casa_server.ts';
import { approveCloudDevice, cloudLink, cloudSpaceKey, myPublicKey, readCloudKeys, recoverCloudKey, renewCloudKey, setUpCloudSecrets, spaceJoinKey, type CloudDevice, type CloudKeys, type NewKeys } from '../data/cloud_secrets.ts';
import { requestReload } from '../data/dashboard.ts';
import { readStoreSettings, writeStoreSettings } from '../data/stores.ts';
import { fingerprintOf } from '../secrets/keys.ts';
import { ChromeError, ChromeNote } from '../ui/chrome_page.tsx';
import { ConfirmDialog } from '../ui/confirm_dialog.tsx';
import { closeSettings } from './settings_navigation.ts';
import { AllowDeviceDialog } from './allow_device_dialog.tsx';
import { EmergencyKeyDialog, type SpaceKeyReason } from './space_key_dialog.tsx';
import { SettingsGroup, SettingsRow } from './settings_ui.tsx';

// Settings → "Tilez Cloud" (docs/specs/cloud-joining.md JN-6; secrets SE-13 to SE-15, SE-22, SE-23),
// shown while this device keeps its dashboards on a Tilez Cloud, in plain words: the home code to add a
// device (cloud-spaces SP-4); the devices of the home, a waiting one allowed after comparing its code
// (QSE-7); new keys, with a new emergency key (the recovery key); linking the home to an account;
// deleting it (SP-8). On a device not let in yet: its code, and the emergency key. On the first
// space, before its passwords were protected: protecting them.

function messageOf(error: unknown) : string {
    return error instanceof Error ? error.message : String(error);
}

type State = { status: 'loading' } | { status: 'none' } | { status: 'ready'; link: ServerLink; keys: CloudKeys; myCode: string; codes: Record<string, string>; joinKey: string | undefined; hasAccounts: boolean } | { status: 'failed'; error: string };

/** `onChanged`: the list of parts shows the state anew (encrypted, this device has the key). */
export function SecurityPart({ shell, onChanged }: { shell: Bridge; onChanged: () => void }) : ReactNode {
    const [state, setState] = useState<State>({ status: 'loading' });
    const [revision, setRevision] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const [shownKeys, setShownKeys] = useState<{ keys: NewKeys; reason: SpaceKeyReason }>();
    const [deleting, setDeleting] = useState(false);
    const [linkCode, setLinkCode] = useState<{ code: string; expires: string; url: string }>();
    const [linkError, setLinkError] = useState<string>();
    const [approving, setApproving] = useState<CloudDevice>();
    const [typed, setTyped] = useState('');
    const [gotKey, setGotKey] = useState(false);
    /** Whether this device was waiting for the key when last looked: approved since, it is offered the reload. */
    const wasWaiting = useRef(false);

    useEffect(() => {
        let isCurrent = true;

        void (async () => {
            try {
                const link = await cloudLink(shell);

                if (!link) {
                    return isCurrent && setState({ status: 'none' });
                }

                // Says this device's key first, so that a device that joined is seen by the others (SE-13).
                await cloudSpaceKey(shell, link);

                const keys = await readCloudKeys(shell, link);
                const myCode = await fingerprintOf(await myPublicKey());
                const codes = Object.fromEntries(await Promise.all(keys.devices.map(async (device) => [device.id, await fingerprintOf(device.publicKey)] as const)));
                const joinKey = keys.mine ? await spaceJoinKey(shell, link) : undefined;
                // Linking to an account is offered where Tilez Cloud has accounts (cloud-accounts AC-11).
                const hasAccounts = (await readServerInfo(shell, link).catch(() => undefined))?.signIn.includes('account') ?? false;

                if (isCurrent) {
                    if (wasWaiting.current && keys.mine) {
                        setGotKey(true);
                    }

                    wasWaiting.current = keys.keyVersion > 0 && !keys.mine;
                    setState({ status: 'ready', link, keys, myCode, codes, joinKey, hasAccounts });
                }
            } catch (caught) {
                if (isCurrent) {
                    setState({ status: 'failed', error: caught instanceof ServerError && caught.status === 401 ? 'This device is no longer in a space on Tilez Cloud: it was removed, or the space was deleted. To use Tilez Cloud again, add it in Dashboard Store.' : messageOf(caught) });
                }
            }
        })();

        return () => {
            isCurrent = false;
        };
    }, [shell, revision]);

    const refresh = () : void => {
        setRevision((value) => value + 1);
        onChanged();
    };

    const run = async (action: () => Promise<void>) : Promise<void> => {
        setBusy(true);
        setError(undefined);

        try {
            await action();
        } catch (caught) {
            setError(messageOf(caught));
        } finally {
            setBusy(false);
        }
    };

    if (state.status === 'loading') {
        return <Spinner m="4" />;
    }

    if (state.status === 'failed') {
        return <ChromeError text={state.error} />;
    }

    if (state.status === 'none') {
        return (
            <SettingsGroup note="This device keeps its dashboards elsewhere. To use Tilez Cloud: Dashboard Store → Add Tilez Cloud.">
                <SettingsRow title="Not on Tilez Cloud" />
            </SettingsGroup>
        );
    }

    const { link, keys, myCode, codes, joinKey, hasAccounts } = state;
    const reloadRow = gotKey ? (
        <SettingsRow
            icon="arrows-rotate"
            title="Reload the dashboard"
            detail="So that it can use its passwords now"
            onClick={() => {
                requestReload();
                closeSettings();
            }}
        />
    ) : null;

    // A home from before its passwords were protected: tilez-cloud's first space (SE-21).
    if (keys.keyVersion === 0) {
        return (
            <Stack gap="6">
                <SettingsGroup title="Protect your passwords" note="The passwords your dashboards use are kept on Tilez Cloud as they are. Protecting them makes a key that only your devices have: then nobody else can read them, not even Tilez Cloud.">
                    <SettingsRow icon="lock" title="Protect them" onClick={() => void run(async () => {
                        const { moved, ...made } = await setUpCloudSecrets(shell, link);
                        setShownKeys({ keys: made, reason: { kind: 'encrypted', moved } });
                    })} end={busy ? <Spinner size="sm" /> : undefined} />
                </SettingsGroup>
                {error ? <ChromeError text={error} /> : null}
                {shownKeys ? <EmergencyKeyDialog keys={shownKeys.keys} reason={shownKeys.reason} onDone={() => {
                    setShownKeys(undefined);
                    refresh();
                }} /> : null}
            </Stack>
        );
    }

    const devices = keys.devices.filter((device) => device.id !== keys.me);

    if (!keys.mine) {
        return (
            <Stack gap="6">
                <SettingsGroup title="Waiting to be let in" note="Open the Tilez app on another device of your home: it asks to let this one in. Check that it shows this code. Until then, the dashboards here ask for their passwords.">
                    <SettingsRow title={<Text as="span" fontFamily="mono" fontSize="lg">{myCode}</Text>} detail="This device's code" />
                    <SettingsRow icon="arrows-rotate" title="Check again" onClick={refresh} opens={false} />
                </SettingsGroup>
                <SettingsGroup title="Lost your other devices?" note="Type the emergency key you wrote down when your home was started.">
                    <Stack gap="3" p="4">
                        <Input placeholder="XXXX-XXXX-XXXX-…" value={typed} onChange={(event) => setTyped(event.target.value)} aria-label="Emergency key" fontFamily="mono" />
                        <Button
                            colorPalette="blue"
                            alignSelf="flex-start"
                            disabled={typed.trim() === ''}
                            loading={busy}
                            onClick={() => void run(async () => {
                                await recoverCloudKey(shell, link, typed);
                                setTyped('');
                                setGotKey(true);
                                refresh();
                            })}
                        >
                            Use it
                        </Button>
                    </Stack>
                </SettingsGroup>
                {error ? <ChromeError text={error} /> : null}
            </Stack>
        );
    }

    const removedNames = keys.removed.map((device) => `"${device.name}"`).join(', ');
    const renew = () : Promise<void> => run(async () => setShownKeys({ keys: await renewCloudKey(shell, link), reason: { kind: 'renewed' } }));

    return (
        <Stack gap="6">
            {gotKey ? <ChromeNote status="success" text="This device is in your home now." /> : null}
            {reloadRow ? <SettingsGroup>{reloadRow}</SettingsGroup> : null}
            {keys.removed.length > 0 ? (
                <SettingsGroup title="Make new keys" note={`${removedNames} left your home. It may still know the passwords saved until now; new keys keep it out of those saved from now on.`}>
                    <SettingsRow icon="key" title="Make new keys" detail="And a new emergency key" onClick={() => void renew()} end={busy ? <Spinner size="sm" /> : undefined} />
                </SettingsGroup>
            ) : null}
            <SettingsGroup title="Add a device" note="On the new device: Dashboard Store → Add Tilez Cloud → “No, I have one already” → type this code. Then allow it here when it asks.">
                <SettingsRow title={<Text as="span" fontFamily="mono" fontSize="2xl" data-testid="shown-space-key">{joinKey ?? 'Not known on this device'}</Text>} detail="Home code" />
            </SettingsGroup>
            <SettingsGroup title="Devices in your home">
                {devices.length === 0 ? <SettingsRow title={<Text as="span" color="fg.muted">Only this one</Text>} /> : null}
                {devices.map((device) => (
                    <SettingsRow
                        key={device.id}
                        icon={device.hasKey ? 'mobile-screen' : 'clock'}
                        title={device.name}
                        detail={device.hasKey ? 'In your home' : 'Waiting to be let in'}
                        opens={false}
                        end={
                            device.hasKey ? undefined : (
                                <HStack>
                                    <Button size="xs" colorPalette="blue" onClick={() => setApproving(device)}>
                                        Allow
                                    </Button>
                                </HStack>
                            )
                        }
                    />
                ))}
                <SettingsRow icon="arrows-rotate" title="Check again" onClick={refresh} opens={false} />
            </SettingsGroup>
            {keys.removed.length === 0 ? (
                <SettingsGroup title="Emergency key" note="If you lose all your devices, the emergency key you wrote down gets your passwords back. Lost a device, or think someone else knows your emergency key or home code? Make new ones.">
                    <SettingsRow icon="key" title="Make new keys" detail="A new emergency key and home code" onClick={() => void renew()} end={busy ? <Spinner size="sm" /> : undefined} />
                </SettingsGroup>
            ) : null}
            {hasAccounts ? (
                <SettingsGroup title="Link to an account" note={linkCode ? `On ${linkCode.url}, sign in, choose “Add a home” and type this code.` : 'With a Tilez Cloud account you can see your home and its devices in a browser, and add devices without the home code.'}>
                    {linkCode ? <SettingsRow title={<Text as="span" fontFamily="mono" fontSize="xl" data-testid="link-code">{linkCode.code}</Text>} detail={`Until ${new Date(linkCode.expires).toLocaleTimeString()}`} /> : null}
                    <SettingsRow icon="link" title={linkCode ? 'Make another code' : 'Make a code'} onClick={() => void makeLinkCode(shell, link).then(
                        (made) => {
                            setLinkError(undefined);
                            setLinkCode(made);
                        },
                        (caught: unknown) => setLinkError(messageOf(caught)),
                    )} opens={false} />
                    {linkError ? <ChromeError text={linkError} /> : null}
                </SettingsGroup>
            ) : null}
            <SettingsGroup note="Deletes your home on Tilez Cloud with its dashboards, and signs out all its devices. There is no undo.">
                <SettingsRow icon="trash" title="Delete this home" opens={false} color="#f87171" onClick={() => setDeleting(true)} />
            </SettingsGroup>
            {deleting ? (
                <ConfirmDialog
                    title="Delete this home?"
                    message="Every dashboard of your home on Tilez Cloud is deleted, with the passwords it uses, and every device of the home is signed out. Dashboards kept elsewhere stay. There is no undo."
                    confirm="Delete"
                    cancel="Keep it"
                    onConfirm={() => {
                        setDeleting(false);
                        void run(async () => {
                            await deleteCloudSpace(shell, link);
                            const settings = await readStoreSettings(shell);
                            if (settings.current === 'cloud') {
                                await writeStoreSettings(shell, { ...settings, current: 'device' });
                            }
                            refresh();
                        });
                    }}
                    onCancel={() => setDeleting(false)}
                />
            ) : null}
            {error ? <ChromeError text={error} /> : null}
            {shownKeys ? <EmergencyKeyDialog keys={shownKeys.keys} reason={shownKeys.reason} onDone={() => {
                    setShownKeys(undefined);
                    refresh();
                }} /> : null}
            {approving ? (
                <AllowDeviceDialog
                    device={approving}
                    code={codes[approving.id] ?? ''}
                    onClose={() => setApproving(undefined)}
                    onAllow={async () => {
                        await approveCloudDevice(shell, link, approving);
                        setApproving(undefined);
                        refresh();
                    }}
                />
            ) : null}
        </Stack>
    );
}
